import type { ReactElement } from 'react'

// Ícones do canal de venda — marcas SVG embutidas (sem depender de arquivo externo).
// Bling ganha ícone próprio (raio); marketplaces usam a marca reconhecível.
export type ChannelKind = 'meli' | 'tiktok' | 'tray' | 'bling' | 'other'

// `orderNumber` = número do pedido no marketplace (numeroLoja). Quando o canal
// não veio pelo nome (mapa de loja.id), o FORMATO do número identifica: Tray usa
// números curtos, Mercado Livre "2000…", TikTok "585…" (18 díg).
export function channelKind(name: string | null, orderNumber?: string | null, nfNumber?: string | null): ChannelKind {
  const n = (name ?? '').toLowerCase()
  if (/mercado|meli|ebazar|\bml\b/.test(n)) return 'meli'
  if (/tik\s?tok/.test(n)) return 'tiktok'
  if (/tray/.test(n)) return 'tray'
  if (/shopee/.test(n)) return 'other'
  if (/bling/.test(n)) return 'bling'
  // Fallback pelo padrão do número do pedido no marketplace
  const ord = (orderNumber ?? '').toString().trim()
  if (/^585\d{12,}$/.test(ord)) return 'tiktok'
  if (/^200\d{10,}$/.test(ord)) return 'meli'
  // Nº do pedido igual ao da NF: veio da nota, sem número de pedido de loja —
  // não dá para saber o canal. Chutar Tray pelo tamanho do número punha venda
  // direta na conta da Tray.
  const nf = (nfNumber ?? '').toString().trim()
  if (nf && ord.replace(/^0+/, '') === nf.replace(/^0+/, '')) return 'other'
  if (/^\d{4,9}$/.test(ord)) return 'tray'
  return 'other'
}

export const channelKindLabel: Record<ChannelKind, string> = {
  meli: 'Mercado Livre', tiktok: 'TikTok', tray: 'Tray', bling: 'Bling', other: 'Outro canal',
}

// Colcheia (nota musical) — usada 3x no TikTok pro efeito glitch ciano/magenta.
function Note({ fill, dx = 0, dy = 0 }: { fill: string; dx?: number; dy?: number }) {
  return (
    <g fill={fill} transform={`translate(${dx} ${dy})`}>
      <rect x="11.1" y="5.8" width="1.7" height="8.2" rx="0.6" />
      <path d="M12.8 5.8c1.7.5 3 1.8 3.6 3.4-1.2-.8-2.4-1.2-3.6-1.3z" />
      <circle cx="9" cy="13.8" r="2.8" />
    </g>
  )
}

const ICONS: Record<ChannelKind, ReactElement> = {
  meli: (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
      <rect width="22" height="22" rx="5" fill="#FFE600" />
      <text x="11" y="14.6" textAnchor="middle" fontSize="8.5" fontWeight="800" fill="#2D3277" fontFamily="Arial, Helvetica, sans-serif">ML</text>
    </svg>
  ),
  tiktok: (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
      <rect width="22" height="22" rx="5" fill="#010101" />
      <Note fill="#25F4EE" dx={-0.8} dy={-0.6} />
      <Note fill="#FE2C55" dx={0.8} dy={0.6} />
      <Note fill="#FFFFFF" />
    </svg>
  ),
  tray: (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
      <rect width="22" height="22" rx="5" fill="#E6007E" />
      <path d="M6.8 9h8.4l-.8 6.8a1 1 0 0 1-1 .9H8.6a1 1 0 0 1-1-.9z" fill="#fff" />
      <path d="M8.8 9a2.2 2.2 0 0 1 4.4 0" fill="none" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  bling: (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
      <rect width="22" height="22" rx="5" fill="#12B886" />
      <path d="M12.2 4 6.8 12.2H10l-1 5.8 6-9h-3.4z" fill="#fff" />
    </svg>
  ),
  other: (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
      <rect width="22" height="22" rx="5" fill="#E5E7EB" />
      <path d="M6.5 8.2 7.6 6h6.8l1.1 2.2M6.7 8.2h8.6v7H6.7z" fill="none" stroke="#6B7280" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  ),
}

/** Ícone do canal a partir do nome da loja (originChannel) ou do padrão do nº do
 *  pedido no marketplace. Tooltip mostra o nome cru. */
export function ChannelIcon({ name, orderNumber, nfNumber }: {
  name: string | null; orderNumber?: string | null; nfNumber?: string | null
}) {
  const k = channelKind(name, orderNumber, nfNumber)
  return (
    <span title={name || channelKindLabel[k]} className="inline-flex items-center">
      {ICONS[k]}
    </span>
  )
}
